import { useState, Suspense } from 'react';
import { motion, AnimatePresence, type Variants } from 'framer-motion';
import { CheckCircle, XCircle, Clock, RefreshCw } from 'lucide-react';
import { graphql, useLazyLoadQuery, useMutation } from 'react-relay';
import { Button } from '@lecartte/shared-ui/components/Button';
import { Badge } from '@lecartte/shared-ui/components/Badge';
import type { ApprovalsPageQuery } from '@/__generated__/ApprovalsPageQuery.graphql';
import type { ApprovalsPageApproveMutation } from '@/__generated__/ApprovalsPageApproveMutation.graphql';
import type { ApprovalsPageRejectMutation } from '@/__generated__/ApprovalsPageRejectMutation.graphql';

const QUERY = graphql`
  query ApprovalsPageQuery {
    adminPendingAccounts {
      id
      name
      email
      emailVerified
      restaurantName
      restaurantSlug
      createdAt
    }
  }
`;

const APPROVE_MUTATION = graphql`
  mutation ApprovalsPageApproveMutation($input: AdminApproveAccountInput!) {
    AdminApproveAccount(input: $input) {
      success
      error
    }
  }
`;

const REJECT_MUTATION = graphql`
  mutation ApprovalsPageRejectMutation($input: AdminRejectAccountInput!) {
    AdminRejectAccount(input: $input) {
      success
      error
    }
  }
`;

function formatDate(iso: string): string {
  return new Date(iso).toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function ApprovalsContent({ fetchKey }: { fetchKey: number }) {
  const [processing, setProcessing] = useState<string | null>(null);
  const [dismissed, setDismissed] = useState<Set<string>>(new Set());
  const [commitApprove] = useMutation<ApprovalsPageApproveMutation>(APPROVE_MUTATION);
  const [commitReject] = useMutation<ApprovalsPageRejectMutation>(REJECT_MUTATION);

  const data = useLazyLoadQuery<ApprovalsPageQuery>(
    QUERY,
    {},
    { fetchKey, fetchPolicy: 'network-only' }
  );

  const accounts = data.adminPendingAccounts.filter((a) => !dismissed.has(a.id));

  const handleApprove = ({ id }: { id: string }) => {
    setProcessing(id);
    commitApprove({
      variables: { input: { userId: id } },
      onCompleted: (res) => {
        if (res.AdminApproveAccount.success) {
          setDismissed((prev) => new Set([...prev, id]));
        }
        setProcessing(null);
      },
      onError: () => setProcessing(null),
    });
  };

  const handleReject = ({ id }: { id: string }) => {
    setProcessing(id);
    commitReject({
      variables: { input: { userId: id } },
      onCompleted: (res) => {
        if (res.AdminRejectAccount.success) {
          setDismissed((prev) => new Set([...prev, id]));
        }
        setProcessing(null);
      },
      onError: () => setProcessing(null),
    });
  };

  if (accounts.length === 0) {
    return (
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-10 flex flex-col items-center gap-3 text-center">
        <div className="size-12 rounded-full bg-green-100 flex items-center justify-center">
          <CheckCircle size={24} className="text-green-600" />
        </div>
        <p className="font-semibold text-slate-900">Nenhuma conta pendente</p>
        <p className="text-slate-400 text-sm">Todas as contas foram revisadas.</p>
      </div>
    );
  }

  const cardVariants: Variants = {
    hidden: { opacity: 0, x: -16 },
    show: { opacity: 1, x: 0, transition: { duration: 0.3, ease: 'easeOut' } },
    exit: { opacity: 0, x: 40, scale: 0.97, transition: { duration: 0.25, ease: 'easeIn' } },
  };

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center gap-2">
        <Clock size={14} className="text-amber-500" />
        <span className="text-sm text-slate-500">
          <span className="font-semibold text-slate-900">{accounts.length}</span>{' '}
          {accounts.length === 1 ? 'conta aguardando' : 'contas aguardando'} aprovação
        </span>
      </div>

      <AnimatePresence initial={false}>
      {accounts.map((account) => (
        <motion.div
          key={account.id}
          variants={cardVariants}
          initial="hidden"
          animate="show"
          exit="exit"
          layout
          className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5 flex items-center gap-4"
        >
          <div className="size-10 rounded-full bg-amber-100 flex items-center justify-center shrink-0">
            <span className="text-sm font-bold text-amber-700">
              {account.name[0]?.toUpperCase()}
            </span>
          </div>

          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <p className="font-semibold text-slate-900 truncate">{account.name}</p>
              {account.emailVerified ? (
                <Badge label="Email verificado" variant="success" />
              ) : (
                <Badge label="Email não verificado" variant="warning" />
              )}
            </div>
            <p className="text-sm text-slate-500 truncate">{account.email}</p>
            {account.restaurantName && (
              <p className="text-xs text-slate-400 mt-0.5">
                Restaurante:{' '}
                <span className="font-medium text-slate-600">{account.restaurantName}</span>
                {account.restaurantSlug && (
                  <span className="text-slate-400"> · @{account.restaurantSlug}</span>
                )}
              </p>
            )}
            <p className="text-xs text-slate-400 mt-1">
              Solicitado em {formatDate(account.createdAt)}
            </p>
          </div>

          <div className="flex items-center gap-2 shrink-0">
            <Button
              variant="outline"
              size="sm"
              disabled={processing === account.id}
              onClick={() => handleReject({ id: account.id })}
              className="text-red-600 border-red-200 hover:bg-red-50"
            >
              <XCircle size={14} />
              Rejeitar
            </Button>
            <Button
              size="sm"
              disabled={processing === account.id}
              onClick={() => handleApprove({ id: account.id })}
              className="bg-success-600 hover:bg-success-500"
            >
              <CheckCircle size={14} />
              Aprovar
            </Button>
          </div>
        </motion.div>
      ))}
      </AnimatePresence>
    </div>
  );
}

export function ApprovalsPage() {
  const [fetchKey, setFetchKey] = useState(0);

  return (
    <div className="flex flex-col gap-6">
      <div className="flex items-center justify-end">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setFetchKey((k) => k + 1)}
        >
          <RefreshCw size={14} />
          Atualizar
        </Button>
      </div>

      <Suspense
        fallback={
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-8 text-center text-slate-400 text-sm">
            Carregando...
          </div>
        }
      >
        <ApprovalsContent fetchKey={fetchKey} />
      </Suspense>
    </div>
  );
}
